import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const ADMIN_EMAILS = ['admin@ridehowl.com', 'support@ridehowl.com'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Response interceptor to handle 401 (unauthorized) globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          setToken(null);
          setUser(null);
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          delete axios.defaults.headers.common['Authorization'];
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check for existing session
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('admin_token');
      const storedUser = localStorage.getItem('admin_user');

      if (storedToken && storedUser) {
        const userData = JSON.parse(storedUser);
        // Verify the user is still an admin
        if (ADMIN_EMAILS.includes(userData.email)) {
          setToken(storedToken);
          setUser(userData);
        } else {
          // Clear non-admin session
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    // Check if email is in admin whitelist before attempting login
    if (!ADMIN_EMAILS.includes(email.toLowerCase())) {
      return {
        success: false,
        error: 'Access denied. This portal is restricted to authorized administrators.'
      };
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        {
          username: email,
          password,
          user_type: 'driver',  // Admin accounts are driver-type for now
          remember_me: false
        },
        {
          timeout: 30000
        }
      );

      const { access_token, user_type, user_id, email: userEmail } = response.data;

      // Double-check email from response
      if (!ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
        return {
          success: false,
          error: 'Access denied. This portal is restricted to authorized administrators.'
        };
      }

      const userData = {
        id: user_id,
        user_type: user_type,
        email: userEmail
      };

      setToken(access_token);
      setUser(userData);
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_user', JSON.stringify(userData));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
          success: false,
          error: 'Login request timed out. Please try again.'
        };
      }

      if (!error.response) {
        return {
          success: false,
          error: 'Unable to connect to server. Please check your connection.'
        };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed. Please check your credentials.'
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const value = {
    user,
    token,
    loading,
    isAdmin,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
